import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UGCLayout from '@/components/layouts/UGCLayout';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { ugcFeedbackService } from '@/services/ugcFeedbackService';

const sampleScripts = [
  {
    label: 'Short intro (30s)',
    text: `Hey, I’m [Name], a UGC creator focused on [niche]. I help brands get scroll-stopping TikToks/Reels with hooks that convert. Today I’m sharing why I do this and what results to expect. Stick around—more tips coming.`
  },
  {
    label: 'Product-led intro',
    text: `Quick intro: I’m [Name]. I create UGC for [niche] brands—think TikTok/Reels with strong hooks, demo shots, and clear CTAs. In this 30s clip I’ll show my style and how I frame benefits fast.`
  },
  {
    label: 'Story hook intro',
    text: `POV: You need UGC that doesn’t look like an ad. I’m [Name], and I craft social-first stories that feel native. Here’s my 30s intro—how I plan hooks, shots, and CTAs to drive clicks.`
  }
];

const UGConboarding = () => {
  const navigate = useNavigate();
  const { profile, updateProfile, refreshProfile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-onboarding', userTier);

  const [selectedScript, setSelectedScript] = useState(sampleScripts[0].text);
  const [videoLibrary, setVideoLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [hasUploadedIntro, setHasUploadedIntro] = useState(false);
  const [recording, setRecording] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [requestingFeedback, setRequestingFeedback] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('ugc_video_library');
    if (stored) setVideoLibrary(JSON.parse(stored));
    const intro = localStorage.getItem('ugc_intro_done') === 'true' || profile?.ugc_onboarding_completed;
    setHasUploadedIntro(Boolean(intro));
  }, [profile]);

  useEffect(() => {
    localStorage.setItem('ugc_video_library', JSON.stringify(videoLibrary));
  }, [videoLibrary]);

  useEffect(() => {
    if (hasUploadedIntro) {
      localStorage.setItem('ugc_intro_done', 'true');
    }
  }, [hasUploadedIntro]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    // Simulate upload for now
    setTimeout(() => {
      const entry = {
        id: crypto.randomUUID(),
        name: file.name,
        createdAt: new Date().toISOString(),
        size: file.size
      };
      setVideoLibrary([entry, ...videoLibrary]);
      setHasUploadedIntro(true);
      setUploading(false);
    }, 800);
  };

  const handleRecordToggle = () => {
    setRecording((prev) => !prev);
    // For now, just a stub—no actual capture, since integrations are pending.
  };

  const handleRequestFeedback = async () => {
    if (!videoLibrary.length) return;
    setRequestingFeedback(true);
    try {
      const latest = videoLibrary[0];
      const res = await ugcFeedbackService.requestFeedback({
        videoId: latest.id,
        url: latest.name,
        userId: profile?.id
      });
      setFeedback(res);
    } catch (err) {
      console.error('Feedback request failed', err);
    } finally {
      setRequestingFeedback(false);
    }
  };

  const handleComplete = async () => {
    if (!hasUploadedIntro || completing) return;
    setCompleting(true);
    try {
      localStorage.setItem('ugc_intro_done', 'true');
      // Try to update profile with onboarding flag
      try {
        await updateProfile({ ugc_onboarding_completed: true, ugc_status: 'active' });
        await refreshProfile();
      } catch (profileErr) {
        console.warn('Could not update profile (column may not exist yet):', profileErr);
        // Continue anyway - localStorage will serve as backup
      }
      navigate('/app/ugc-dashboard');
    } catch (err) {
      console.error('Failed to complete onboarding', err);
      setCompleting(false);
    }
  };

  const renderPreviewBlock = () => (
    <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Icon name="Crown" size={16} /> Creator onboarding (required)
      </h3>
      <p className="text-sm text-amber-100 mt-2">
        Record a 30s intro and upload/post to unlock the UGC platform. Connect TikTok, Instagram, Facebook, and YouTube for direct posting (coming soon).
      </p>
    </div>
  );

  const ugcNavItems = [
    { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
    { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const ugcMoreItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-media', label: 'Media Library', icon: 'Film' },
  ];

  return (
    <UGCLayout>
      <NavigationBar items={ugcNavItems} moreItems={ugcMoreItems} hideLocks />
      <main className="max-w-5xl mx-auto px-4 py-10 mb-20">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs font-semibold border border-amber-500/30 mb-3">
            <Icon name="Video" size={14} />
            UGC Onboarding
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Record your 30s intro</h1>
          <p className="text-amber-100 max-w-2xl">
            Before entering the platform, record and upload a 30-second intro video. Use the sample scripts or your own. Posting to TikTok/IG/FB/YT will be built-in—coming soon.
          </p>
        </div>

        {!canAccess && renderPreviewBlock()}
        {canAccess && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">Steps</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-amber-100">
                <li>Pick a sample script (or write your own) for a 30s intro.</li>
                <li>Record using our teleprompter or your phone; minimum 30 seconds.</li>
                <li>Upload the video here (required to enter).</li>
                <li>(Coming soon) Share directly to TikTok, Instagram, Facebook, YouTube.</li>
                <li>Get Lumo feedback on audio/video to improve future takes (coming soon).</li>
              </ol>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-3">Sample script</h3>
              <div className="flex flex-col gap-3">
                <select
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                  onChange={(e) => {
                    const found = sampleScripts.find(s => s.label === e.target.value);
                    if (found) setSelectedScript(found.text);
                  }}
                >
                  {sampleScripts.map((s) => (
                    <option key={s.label} value={s.label}>{s.label}</option>
                  ))}
                </select>
                <textarea
                  className="w-full h-32 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-amber-100/40"
                  value={selectedScript}
                  onChange={(e) => setSelectedScript(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(selectedScript); }}>
                    Copy script
                  </Button>
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white border-transparent" onClick={() => navigate('/app/teleprompter')}>
                    Open teleprompter
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-3">Record / Upload</h3>
              <div className="flex flex-wrap gap-3 items-center mb-3">
                <Button variant={recording ? 'destructive' : 'outline'} onClick={handleRecordToggle}>
                  <Icon name="Circle" size={14} className={recording ? 'text-red-600' : ''} />
                  {recording ? 'Stop recording (stub)' : 'Record in browser (stub)'}
                </Button>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleUpload}
                  className="text-sm text-white"
                  disabled={uploading}
                />
                <Button variant="ghost" size="sm" onClick={() => alert('Platform posting coming soon')}>
                  Connect TikTok / IG / FB / YT
                </Button>
              </div>
              <p className="text-xs text-amber-100/60">Upload your 30s intro to proceed. Posting and Lumo feedback will arrive soon.</p>
              {uploading && <p className="text-sm text-amber-300 mt-2">Uploading...</p>}
              {hasUploadedIntro && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 text-green-200 text-xs font-semibold">
                  <Icon name="Check" size={14} /> Intro uploaded—you're all set.
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  size="sm"
                  onClick={handleComplete}
                  disabled={!hasUploadedIntro || completing}
                >
                  {completing ? 'Finishing...' : hasUploadedIntro ? 'Enter UGC Dashboard' : 'Complete intro to continue'}
                </Button>
                {!hasUploadedIntro && (
                  <span className="text-xs text-amber-100/60">Upload your intro to unlock the dashboard.</span>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Video library</h3>
                <Button variant="outline" size="sm" onClick={() => setVideoLibrary([])}>
                  Clear list
                </Button>
              </div>
              {videoLibrary.length === 0 && (
                <p className="text-sm text-amber-100/60">No videos yet. Upload your intro to continue.</p>
              )}
              <div className="space-y-2">
                {videoLibrary.map((vid) => (
                  <div key={vid.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-semibold text-white">{vid.name}</p>
                      <p className="text-xs text-amber-100/60">
                        {new Date(vid.createdAt).toLocaleString()} • {Math.round((vid.size || 0) / 1024)} KB
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => alert('Playback coming soon')}>
                      Play
                    </Button>
                  </div>
                ))}
              </div>
              {videoLibrary.length > 0 && (
                <div className="mt-3">
                  <Button size="sm" onClick={handleRequestFeedback} disabled={requestingFeedback}>
                    {requestingFeedback ? 'Requesting feedback...' : 'Request Lumo feedback (stub)'}
                  </Button>
                  {feedback && (
                    <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
                      <p className="font-semibold text-white mb-1">Feedback</p>
                      <p className="text-amber-100">{feedback.summary}</p>
                      <ul className="list-disc list-inside text-sm text-amber-100 mt-2 space-y-1">
                        {(feedback.suggestions || []).map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">Daily streaks & leaderboard</h3>
              <p className="text-sm text-amber-100">
                Recording and posting daily will fuel your streak and leaderboard rank. Lumo will auto-review your audio/video and suggest improvements.
              </p>
            </div>
          </div>
        )}
      </main>
    </UGCLayout>
  );
};

export default UGConboarding;
